import ProductCard from '@c/cards/ProductCard';
import { WishlistIcon } from '@c/icons';
import CheckmarkIconButton from '@c/icons/buttons/CheckmarkButton';
import { DefaultLayout } from '@c/layouts';
import SEO from '@c/SEO';
import Button from '@ui/Button';
import Input from '@ui/Input';
import PaginatorClient from "@ui/Paginator"
import { getProductsByIds } from '@util/firestore/products';
import { ProductDocument } from '@util/types/firestore/products';
import { Pagination } from '@util/types/paginator';
import { useAuth } from 'context/AuthContext';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

const Wishlist = () => {
  const [inStockOnly, setInStockOnly] = useState(false);
  const [onSaleOnly, setOnSaleOnly] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [allProducts, setAllProducts] = useState<ProductDocument[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<ProductDocument[]>(
    []
  );
  const [pageInfo, setPageInfo] = useState<Pagination>({
    page: 1,
    totalPages: 1,
    totalResults: 0,
    itemsPerPage: 10,
  });
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const { userDoc } = useAuth();
  useQuery(
    ['favorites', userDoc?.favorites, pageInfo.page],
    () =>
      getProductsByIds(
        userDoc?.favorites?.slice(
          (pageInfo.page - 1) * 10,
          pageInfo.page * 10
        ) ?? []
      ),
    {
      enabled: !!userDoc?.favorites?.length,
      onSuccess: (data) => {
        setAllProducts(data);
        setPageInfo((prev) => ({
          ...prev,
          totalResults: userDoc?.favorites?.length ?? 0,
          totalPages: Math.ceil((userDoc?.favorites?.length ?? 0) / 10),
        }));
      },
    }
  );

  useEffect(() => {
    if (allProducts?.length) {
      let filteredProducts = allProducts;
      if (inStockOnly) {
        filteredProducts = filteredProducts.filter((product) => {
          return !product.out_of_stock;
        });
      }
      if (onSaleOnly) {
        filteredProducts = filteredProducts.filter((product) => {
          return product.on_sale;
        });
      }
      if (searchValue) {
        filteredProducts = filteredProducts.filter((product) => {
          return product.title
            .toLowerCase()
            .includes(searchValue.toLowerCase());
        });
      }
      setFilteredProducts(filteredProducts);
    } else {
      setFilteredProducts([]);
    }
  }, [allProducts, inStockOnly, onSaleOnly, searchValue]);

  return (
    <>
      <SEO
        title="My Wishlist"
        description="View and manage your saved camera gear on GearFocus."
        noIndex
      />
      <div className="mt-[3rem] flex w-full max-w-[120rem] flex-col px-[1.6rem] lg:px-default-padding-x">
        {/* heading */}
        <h1 className="text-[4.4rem] font-semibold leading-[5.2rem]">
          Wishlist
        </h1>
        {/* toolbar */}
        {allProducts?.length ? (
          <div className="mt-[4.8rem] flex flex-col items-start justify-between gap-[1.6rem] lg:flex-row lg:items-center">
            <div>{pageInfo.totalResults} {pageInfo.totalResults === 1 ? 'Item' : "Items"}</div>

            <div className="flex w-full flex-col gap-[1.6rem] lg:w-fit lg:flex-row">
              <div className="hidden min-w-[30rem] lg:block">
                <Input
                  type="search"
                  placeholder={'Search products...'}
                  onChange={handleSearchChange}
                  full
                />
              </div>
              <div className="block lg:hidden">
                <Input
                  full
                  type="search"
                  placeholder={'Search products...'}
                  onChange={handleSearchChange}
                />
              </div>
              <div className="flex justify-end gap-[1.6rem]">
                <CheckmarkIconButton
                  checked={inStockOnly}
                  onClick={() => {
                    setInStockOnly(!inStockOnly);
                  }}
                  label="In Stock Only"
                ></CheckmarkIconButton>
                <CheckmarkIconButton
                  checked={onSaleOnly}
                  onClick={() => {
                    setOnSaleOnly(!onSaleOnly);
                  }}
                  label="On Sale"
                ></CheckmarkIconButton>
              </div>
            </div>
          </div>
        ) : (
          <></>
        )}

        {/* Products */}
        <div className="mt-[2.4rem]">
          {filteredProducts?.length ? (
            <>
              <div className="grid grid-cols-2 gap-[2rem] md:grid-cols-3 lg:grid-cols-4">
                {filteredProducts?.map((product) => (
                  <ProductCard
                    item={product}
                    key={product.id}
                    variant="xSmall"
                  />
                ))}
              </div>
              <div className="mt-[2.4rem]">
                <PaginatorClient
                  currentPage={pageInfo.page}
                  totalPages={pageInfo.totalPages}
                  onPageChange={(page) => {
                    setPageInfo((prev) => ({ ...prev, page }));
                  }}
                />
              </div>
            </>
          ) : (
            <div className="flex min-h-[40rem] w-full flex-col items-center justify-center gap-[1.6rem]">
              <WishlistIcon />
              <h1 className="text-[2.4rem] font-semibold leading-[3.2rem] text-brand-light-black">
                Empty Wishlist
              </h1>
              <span className="text-center text-brand-gray">
                {allProducts.length
                  ? 'No items matching these filters'
                  : "You don't have any items in your wishlist, yet."}
              </span>
              <Button type="secondary" text="Shop now" href="/" />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

Wishlist.getInitialProps = async () => {
  return { title: 'Wishlist' };
};
Wishlist.getLayout = function getLayout(page: React.ReactElement) {
  return <DefaultLayout>{page}</DefaultLayout>;
};

export default Wishlist;
